import React, { useEffect, useMemo, useState } from "react";
import io from "socket.io-client";
import { GoDotFill } from "react-icons/go";
import DashboardServices from "../../../services/DashboardServices";
import OutletUsersServices from "../../../services/OutletUsersServices";
import moment from "moment";

const DashboardAdmin = ({ user, data }) => {
  const stats = data?.stats || {};
  const settings = data?.settings;
  const recentWinners = data?.recentWinners || [];
  const wallet = data?.wallet;
  const walletBalance = Number(wallet?.wallet_balance || 0);
  const initialAdminReport = data?.adminReport || null;
  const fmt = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  const fmtSigned = (n) => {
    const v = Number(n || 0);
    const abs = Math.abs(v);
    const core = `₹${abs.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
    return v < 0 ? `- ${core}` : core;
  };

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [storeId, setStoreId] = useState("");
  const [shops, setShops] = useState([]);
  const [adminReport, setAdminReport] = useState(initialAdminReport);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  const [gameStatus, setGameStatus] = useState({
    game_3min: true,
    game_5min: true,
  });

  useEffect(() => {
    if (
      settings &&
      (settings.game_3min !== undefined || settings.game_5min !== undefined)
    ) {
      setGameStatus((prev) => ({
        ...prev,
        ...(settings.game_3min !== undefined && {
          game_3min: !!settings.game_3min,
        }),
        ...(settings.game_5min !== undefined && {
          game_5min: !!settings.game_5min,
        }),
      }));
    }
  }, [data?.settings]);

  useEffect(() => {
    const s = io(process.env.REACT_APP_NODE_URL, { transports: ["websocket"] });
    s.on("gameStatusChange", (payload) => {
      if (
        payload &&
        (payload.game_3min !== undefined || payload.game_5min !== undefined)
      ) {
        setGameStatus((prev) => ({
          ...prev,
          ...(payload.game_3min !== undefined && {
            game_3min: !!payload.game_3min,
          }),
          ...(payload.game_5min !== undefined && {
            game_5min: !!payload.game_5min,
          }),
        }));
      }
    });
    return () => s.disconnect();
  }, []);

  useEffect(() => {
    if (Number(user?.role_id) !== 2) return;
    const api = new OutletUsersServices();
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAllOutletuserData(1, 1000, "");
        const list = res?.data || [];
        if (cancelled) return;
        setShops(list);
      } catch (e) {
        if (cancelled) return;
        setShops([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.role_id]);

  useEffect(() => {
    if (Number(user?.role_id) !== 2) return;
    let cancelled = false;

    (async () => {
      setLoadingReport(true);
      try {
        const res = await DashboardServices.getAdminReportSummaryByFilter({
          fromDate,
          toDate,
          storeId: storeId || undefined,
        });
        if (cancelled) return;
        if (res?.isSuccess) setAdminReport(res?.data);
      } catch (e) {
      } finally {
        if (!cancelled) setLoadingReport(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.role_id, fromDate, toDate, storeId]);

  useEffect(() => {
    if (Number(user?.role_id) !== 2) return;
    let cancelled = false;

    (async () => {
      setLoadingActivity(true);
      try {
        const res = await DashboardServices.getAdminRecentActivity({
          limit: 15,
          storeId: storeId || undefined,
        });
        if (cancelled) return;
        if (res?.isSuccess) setRecentActivity(res?.data || []);
      } catch (e) {
        if (!cancelled) setRecentActivity([]);
      } finally {
        if (!cancelled) setLoadingActivity(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.role_id, storeId]);

  return (
    <>
      {Number(user?.role_id) === 2 && (
        <div className="dashboard-section" style={{ marginBottom: 18 }}>
          <h2 style={{ marginBottom: 10 }}>Daily Summary</h2>
          <div className="settings-summary" style={{ alignItems: "center" }}>
            <span>
              <strong>From</strong>{" "}
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </span>
            <span>
              <strong>To</strong>{" "}
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </span>
            <span>
              <strong>Shop</strong>{" "}
              <select
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
              >
                <option value="">All Shops</option>
                {shops.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name} ({s.username})
                  </option>
                ))}
              </select>
            </span>
            <span style={{ color: "#64748b" }}>
              {loadingReport ? "Loading..." : ""}
            </span>
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Wallet Balance</h3>
            <p className="stat-value">₹{walletBalance.toFixed(2)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔥</div>
          <div className="stat-content">
            <h3>Running Sessions</h3>
            <p className="stat-game-value">
              3min{" "}
              <span className={gameStatus.game_3min ? "live" : "stopped"}>
                <GoDotFill />
                {gameStatus.game_3min ? "Live" : "Stopped"}
              </span>
            </p>
            <p className="stat-game-value">
              5min{" "}
              <span className={gameStatus.game_5min ? "live" : "stopped"}>
                <GoDotFill />
                {gameStatus.game_5min ? "Live" : "Stopped"}
              </span>
            </p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Today Sessions</h3>
            <p className="stat-value">{stats.todaySessionsCount ?? 0}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <h3>Today Winners</h3>
            <p className="stat-value">{stats.todayWinnersCount ?? 0}</p>
          </div>
        </div>

        {user?.role_id === 2 && adminReport && (
          <>
            <div className="stat-card">
              <div className="stat-icon">📥</div>
              <div className="stat-content">
                <h3>Add Balance</h3>
                <p className="stat-value">{fmt(adminReport.add_balance)}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📤</div>
              <div className="stat-content">
                <h3>Deduct Balance</h3>
                <p className="stat-value">{fmt(adminReport.deduct_balance)}</p>
              </div>
            </div>

            {/* <div className="stat-card">
              <div className="stat-icon">🧮</div>
              <div className="stat-content">
                <h3>Net Balance</h3>
                <p className="stat-value">{fmtSigned(adminReport.net_balance)}</p>
              </div>
            </div> */}

            <div className="stat-card">
              <div className="stat-icon">🧾</div>
              <div className="stat-content">
                <h3>Purchase</h3>
                <p className="stat-value">{fmt(adminReport.purchase)}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <h3>Win</h3>
                <p className="stat-value">{fmt(adminReport.win)}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                <h3>Game Profit</h3>
                <p className="stat-value">{fmtSigned(adminReport.game_profit)}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">↩️</div>
              <div className="stat-content">
                <h3>Total Cancel</h3>
                <p className="stat-value">{fmt(adminReport.cancel)}</p>
              </div>
            </div>
          </>
        )}
        {/* <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total Users</h3>
            <p className="stat-value">{stats.totalUsersCount ?? 0}</p>
          </div>
        </div> */}
      </div>

      {Number(user?.role_id) === 2 && (
        <div className="recent-activity">
          <h2>Recent Activity</h2>
          {loadingActivity ? (
            <p className="activity-empty">Loading...</p>
          ) : recentActivity.length === 0 ? (
            <p className="activity-empty">No recent activity.</p>
          ) : (
            <div className="activity-list">
              {recentActivity.map((a) => {
                const amt = Number(a.amount || 0);
                const wp = a.wallet_payment;
                const isReceipt =
                  wp === "Receipt" ||
                  (wp == null && String(a.txn_type || "").toUpperCase() === "CREDIT");
                const cls = isReceipt ? "won" : "lost";
                const who =
                  a.from_username ||
                  a.from_name ||
                  (a.from_id != null ? a.from_id : a.reference_type);
                const to = a.to_username || a.to_name || a.user_id;
                return (
                  <div className="activity-item" key={a.id}>
                    <span className="activity-time">
                      {a.created_at ? moment
                          .utc(a.created_at)
                          .local()
                          .format("DD/MM/YYYY hh:mm A") : "-"}
                    </span>
                    <span className="activity-desc">
                      {who} → {to} ({a.wallet_payment || a.txn_type || a.reference_type || "-"})
                    </span>
                    <span className={`activity-result ${cls}`}>
                      {isReceipt ? "+" : "-"} {fmt(amt)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DashboardAdmin;
